import type {
  MediaAsset,
  PlaylistItem,
  PlaylistRevision,
} from "@led-signage/shared/management-api";
import type { ValidationResult } from "@led-signage/shared/common";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, usingFixtures } from "./api/index.js";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { useResource, type Resource } from "./api/useResource.js";
import {
  appendItem,
  editorDurationMs,
  moveItem,
  removeItem,
  revisionInput,
  updateItem,
} from "./playlist-editor.js";

function Gate<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (value: T) => ReactNode;
}) {
  if (resource.state === "loading") {
    return <p className="resource-note">読み込み中…</p>;
  }
  if (resource.state === "error") {
    return (
      <div className="inline-alert">
        <b>読み込めませんでした</b>
        <span>{resource.message}</span>
      </div>
    );
  }
  return <>{children(resource.value)}</>;
}

function durationLabel(milliseconds: number | null): string {
  if (milliseconds === null) return "計算中";
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, "0")}`;
}

type ActionState =
  | { kind: "idle" }
  | { kind: "running"; label: string }
  | { kind: "success"; message: string }
  | { kind: "failed"; failure: ActionFailure };

export function PlaylistSection() {
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const playlists = useResource(
    (signal) => api.listPlaylists(signal),
    [reloadToken],
  );
  const detail = useResource(
    (signal) =>
      selectedId === null ? Promise.resolve(null) : api.getPlaylist(selectedId, signal),
    [selectedId, reloadToken],
  );
  const media = useResource(
    (signal) => api.listMedia({ status: "ready" }, signal),
    [reloadToken],
  );
  const [revision, setRevision] = useState<PlaylistRevision | null>(null);
  const [items, setItems] = useState<PlaylistItem[]>([]);
  const [defaultAudioEnabled, setDefaultAudioEnabled] = useState(false);
  const [mediaToAdd, setMediaToAdd] = useState("");
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [dirty, setDirty] = useState(false);
  const [action, setAction] = useState<ActionState>({ kind: "idle" });
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const createPlaylistKey = useRef<string | null>(null);
  const createRevisionKey = useRef<string | null>(null);

  useEffect(() => {
    if (
      playlists.state === "ready" &&
      playlists.value.items.length > 0 &&
      !playlists.value.items.some((item) => item.id === selectedId)
    ) {
      setSelectedId(playlists.value.items[0]?.id ?? null);
    }
  }, [playlists, selectedId]);

  useEffect(() => {
    const latest = detail.state === "ready" ? detail.value?.latestRevision ?? null : null;
    setRevision(latest);
    setItems(latest === null ? [] : [...latest.items]);
    setDefaultAudioEnabled(latest?.defaultAudioEnabled ?? false);
    setValidation(null);
    setDirty(false);
    setAction({ kind: "idle" });
    createRevisionKey.current = null;
  }, [detail]);

  const mediaItems = media.state === "ready" ? media.value.items : [];
  const mediaById = useMemo(
    () => new Map(mediaItems.map((asset) => [asset.id, asset] as const)),
    [mediaItems],
  );
  const totalDuration = editorDurationMs(items, mediaItems);
  const editable = revision?.state === "draft";
  const busy = action.kind === "running";

  const fail = (error: unknown) => {
    setAction({ kind: "failed", failure: describeFailure(error) });
  };

  const selectPlaylist = (id: string) => {
    if (id === selectedId || busy) return;
    if (
      dirty &&
      !window.confirm("未保存の変更を破棄して、別のプレイリストを開きますか？")
    ) {
      return;
    }
    setSelectedId(id);
  };

  const createPlaylist = async () => {
    const name = newName.trim();
    if (name.length === 0 || busy) return;
    setAction({ kind: "running", label: "プレイリストを作成中…" });
    try {
      createPlaylistKey.current ??= newIdempotencyKey();
      const created = await api.createPlaylist({ name }, createPlaylistKey.current);
      createPlaylistKey.current = null;
      setNewName("");
      setShowCreate(false);
      setSelectedId(created.id);
      setReloadToken((token) => token + 1);
      setAction({ kind: "success", message: "プレイリストを作成しました。" });
    } catch (error) {
      fail(error);
    }
  };

  const createDraft = async () => {
    if (selectedId === null || busy) return;
    setAction({ kind: "running", label: "下書きを準備中…" });
    try {
      createRevisionKey.current ??= newIdempotencyKey();
      const created = await api.createPlaylistRevision(
        selectedId,
        revision === null ? {} : { sourceRevisionId: revision.id },
        createRevisionKey.current,
      );
      createRevisionKey.current = null;
      setRevision(created);
      setItems([...created.items]);
      setDefaultAudioEnabled(created.defaultAudioEnabled);
      setValidation(null);
      setDirty(false);
      setAction({ kind: "success", message: "編集用の下書きを作成しました。" });
    } catch (error) {
      fail(error);
    }
  };

  const save = async () => {
    if (revision === null || !editable || busy) return;
    setAction({ kind: "running", label: "下書きを保存中…" });
    try {
      const updated = await api.updatePlaylistRevision(
        revision.id,
        revisionInput(defaultAudioEnabled, items),
        revision.etag,
      );
      setRevision(updated);
      setItems([...updated.items]);
      setValidation(null);
      setDirty(false);
      setAction({ kind: "success", message: "下書きを保存しました。LEDにはまだ反映されません。" });
    } catch (error) {
      fail(error);
    }
  };

  const validate = async () => {
    if (revision === null || busy) return;
    setAction({ kind: "running", label: "公開前検査を実行中…" });
    try {
      const result = await api.validatePlaylistRevision(revision.id);
      setValidation(result);
      setAction({
        kind: "success",
        message: result.valid
          ? "公開前検査に合格しました。"
          : "修正が必要な項目があります。",
      });
    } catch (error) {
      fail(error);
    }
  };

  return (
    <>
      <div className="admin-heading">
        <div>
          <p className="eyebrow eyebrow--blue">MANAGEMENT</p>
          <h1>プレイリスト</h1>
          <p>動画の再生順、繰り返し、音声設定を下書きとして安全に編集します。</p>
        </div>
        <button
          className="admin-primary"
          type="button"
          disabled={busy || dirty}
          onClick={() => { setShowCreate((visible) => !visible); }}
        >
          ＋ 新しいプレイリスト
        </button>
      </div>
      {usingFixtures && (
        <div className="resource-note resource-note--fixture">
          表示中のデータはローカル確認用のサンプルです。
        </div>
      )}
      {showCreate && (
        <section className="panel playlist-create" aria-label="プレイリスト作成">
          <label>
            プレイリスト名
            <input
              value={newName}
              maxLength={120}
              onChange={(event) => {
                setNewName(event.target.value);
                createPlaylistKey.current = null;
              }}
              placeholder="例）通常営業ループ"
            />
          </label>
          <button className="admin-primary" type="button" disabled={busy || newName.trim().length === 0} onClick={() => { void createPlaylist(); }}>
            作成する
          </button>
        </section>
      )}
      <div className="playlist-layout">
        <section className="panel playlist-list">
          <div className="panel__head">
            <div><h2>プレイリスト一覧</h2></div>
          </div>
          <Gate resource={playlists}>{(page) =>
            page.items.length === 0 ? (
              <p className="resource-note">プレイリストはまだありません。</p>
            ) : page.items.map((playlist) => (
              <button
                type="button"
                className={playlist.id === selectedId ? "selected" : ""}
                key={playlist.id}
                disabled={busy}
                onClick={() => { selectPlaylist(playlist.id); }}
              >
                <span className="playlist-icon">▶</span>
                <span><b>{playlist.name}</b><small>{playlist.description ?? "説明なし"}</small></span>
                <em>{playlist.id === selectedId ? "編集中" : "選択"}</em>
              </button>
            ))
          }</Gate>
        </section>

        <section className="panel playlist-editor">
          <Gate resource={detail}>{(value) => {
            if (value === null) {
              return <p className="resource-note">左の一覧からプレイリストを選択してください。</p>;
            }
            return (
              <>
                <div className="editor-head">
                  <div>
                    <span className="draft-label">
                      {revision === null ? "NO REVISION" : `${revision.state.toUpperCase()} REVISION ${String(revision.revisionNumber)}`}
                    </span>
                    <h2>{value.playlist.name}</h2>
                    <p>保存と公開は別操作です。下書き保存だけではLEDへ反映されません。</p>
                  </div>
                  <div>
                    {editable ? (
                      <>
                        <button type="button" className="secondary-button" disabled={busy} onClick={() => { void save(); }}>下書きを保存</button>
                        <button type="button" className="admin-primary" disabled={busy || dirty} onClick={() => { void validate(); }}>{dirty ? "保存後に検査" : "公開前検査"}</button>
                      </>
                    ) : (
                      <button type="button" className="admin-primary" disabled={busy} onClick={() => { void createDraft(); }}>編集用の下書きを作る</button>
                    )}
                  </div>
                </div>

                {action.kind === "running" && <p className="resource-note">{action.label}</p>}
                {action.kind === "success" && <p className="action-note action-note--success">{action.message}</p>}
                {action.kind === "failed" && (
                  <div className="inline-alert">
                    <b>操作に失敗しました</b><span>{action.failure.message}</span>
                    <AuthenticationRecoveryButton failure={action.failure} />
                    {action.failure.reloadHelps && <button className="secondary-button" type="button" onClick={() => { setReloadToken((token) => token + 1); }}>最新の内容を再読み込み</button>}
                  </div>
                )}
                {dirty && <p className="action-note action-note--pending">未保存の変更があります。公開前検査の前に下書きを保存してください。</p>}

                <div className="playlist-summary">
                  <span><b>{items.length}</b> 本の動画</span>
                  <span><b>{durationLabel(totalDuration)}</b> 合計時間</span>
                  <label className={defaultAudioEnabled ? "audio-toggle audio-toggle--warning" : "audio-toggle"}>
                    <input type="checkbox" checked={defaultAudioEnabled} disabled={!editable || busy} onChange={(event) => { setDefaultAudioEnabled(event.target.checked); setValidation(null); setDirty(true); }} />
                    既定音声 {defaultAudioEnabled ? "ON" : "OFF"}
                  </label>
                </div>
                {defaultAudioEnabled && <div className="warning-banner warning-banner--compact"><span>!</span><div><b>音声が出力される可能性があります</b><p>現地設備の音量と動画ごとの設定を確認してください。</p></div></div>}

                <ol className="editor-list">
                  {items.map((item, index) => {
                    const asset: MediaAsset | undefined = mediaById.get(item.mediaAssetId);
                    return (
                      <li key={`${item.mediaAssetId}-${String(index)}`}>
                        <span className="item-order-actions">
                          <button type="button" aria-label={`${String(index + 1)}番目を上へ`} disabled={!editable || busy || index === 0} onClick={() => { setItems(moveItem(items, index, index - 1)); setValidation(null); setDirty(true); }}>↑</button>
                          <button type="button" aria-label={`${String(index + 1)}番目を下へ`} disabled={!editable || busy || index === items.length - 1} onClick={() => { setItems(moveItem(items, index, index + 1)); setValidation(null); setDirty(true); }}>↓</button>
                        </span>
                        <span className="mini-preview mini-preview--blue">{String(index + 1).padStart(2, "0")}</span>
                        <span>
                          <b>{asset?.originalName ?? item.mediaAssetId}</b>
                          <small>{durationLabel(asset?.durationMs ?? null)}・{asset?.hasAudio === true ? "元動画は音声あり" : "音声なし"}</small>
                          <span className="item-settings">
                            <label>繰り返し <input type="number" min={1} max={100} value={item.repeatCount} disabled={!editable || busy} onChange={(event) => { const count = Number(event.target.value); if (Number.isSafeInteger(count) && count >= 1 && count <= 100) { setItems(updateItem(items, item.position, { repeatCount: count })); setDirty(true); } setValidation(null); }} /></label>
                            <label>音声 <select value={item.audioEnabled === null ? "inherit" : item.audioEnabled ? "on" : "off"} disabled={!editable || busy} onChange={(event) => { const value = event.target.value; setItems(updateItem(items, item.position, { audioEnabled: value === "inherit" ? null : value === "on" })); setValidation(null); setDirty(true); }}><option value="inherit">既定を使用</option><option value="off">OFF</option><option value="on">ON</option></select></label>
                          </span>
                        </span>
                        <button type="button" disabled={!editable || busy} onClick={() => { setItems(removeItem(items, item.position)); setValidation(null); setDirty(true); }}>削除</button>
                      </li>
                    );
                  })}
                </ol>
                {editable && (
                  <div className="add-media-row">
                    <select aria-label="追加する動画" value={mediaToAdd} onChange={(event) => { setMediaToAdd(event.target.value); }}>
                      <option value="">動画を選択</option>
                      {mediaItems.map((asset) => <option value={asset.id} key={asset.id}>{asset.originalName}</option>)}
                    </select>
                    <button type="button" className="add-row" disabled={mediaToAdd.length === 0 || busy} onClick={() => { if (mediaToAdd.length > 0) { setItems(appendItem(items, mediaToAdd)); setMediaToAdd(""); setValidation(null); setDirty(true); } }}>＋ 動画を追加</button>
                  </div>
                )}

                {validation !== null && (
                  <section className={validation.valid ? "validation-result validation-result--ok" : "validation-result validation-result--error"} aria-live="polite">
                    <h3>{validation.valid ? "公開前検査：合格" : "公開前検査：修正が必要"}</h3>
                    {validation.errors.map((entry) => <p key={`error-${entry.code}-${entry.field ?? ""}`}>エラー：{entry.message}</p>)}
                    {validation.warnings.map((entry) => <p key={`warning-${entry.code}-${entry.field ?? ""}`}>注意：{entry.message}</p>)}
                    {validation.valid && validation.warnings.length === 0 && <p>プレイリストに問題は見つかりませんでした。</p>}
                  </section>
                )}
              </>
            );
          }}</Gate>
        </section>
      </div>
    </>
  );
}
